import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, takeWhile, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BuilderAppContent } from '../pwa-builder/models/builder-app-content';
import { DateUtils } from './date-utils';
import { LanguageService } from './language.service';
import { AppContent } from './models/app-content';
import { AppLink } from './models/app-link';
import { AppType } from './models/app-type';
import { AppViewMode, SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root',
})
export class ContentService<T extends AppContent> implements BuilderAppContent {
  contentCache = new Map<string, AppContent>();

  /**
   * Map that stores a boolean value (`true`) to indicate preload requests that are ongoing
   */
  contentPreloadRequestStatus = new Map<string, boolean>();

  /**
   * App content is cached as a behaviour subject so that every page of an app
   * can fetch the app content without calling the API again.
   */
  private appContentSource = new BehaviorSubject<T>(null);
  appContent$ = this.appContentSource.asObservable();

  /**
   * This is a map that contains behaviour subjects for a certain property path.
   * eg: theme.colors
   * If at all the value for property path has changed, the behaviour subject inside would emit the updated value
   * A new record would only be added when someone wants to listen to a changes in the value of a certain property (refer to @method listenToSpecificProperty )
   */
  propertyContentChangeMap = new Map<string, BehaviorSubject<any>>();

  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object,
    private languageService: LanguageService,
    private settingsService: SettingsService
  ) {}

  /**
   * Public method used by the different app modules (e.g. educational, reordering, quiz, etc)
   * to fetch the content of the app.
   *
   * The triplet brandSlug, appType and appName uniquely identify one app.
   *
   * @param brandSlug
   * @param appType
   * @param appName
   * @param isMainApp (optional) - default true - determines if the content needs to be cached in the service in the appContent$ variable.
   * @returns
   */
  getAppContent(
    brandSlug: string,
    appType: AppType,
    appName: string,
    isMainApp = true
  ): Observable<T> {
    let optLang = 'en';
    if (isPlatformBrowser(this.platformId)) {
      optLang = this.languageService.getAppLanguage();
    }
    const isPreview = this.settingsService.settings.preview;
    const version = this.settingsService.settings.version;
    return this.fetchAppContent(
      brandSlug,
      appType,
      appName,
      optLang,
      version,
      isPreview
    ).pipe(
      tap((content) => {
        if (isMainApp) {
          // Set the settings only if it's the main app, not for the connected apps
          this.settingsService.setBrandId(content.brandId);
          this.settingsService.setExperienceId(content._id);
          this.settingsService.setExperienceType(content.appType);
          this.settingsService.setExperienceLanguage(content.lang);

          this.appContentSource.next(content);
        }
      })
    );
  }

  /**
   * Fetches content from the CMS service (either Insight CMS or the Legacy CMS, depending on the app type).
   * The triplet brandSlug - appType - appName uniquely identifies one app.
   *
   * @param brandSlug
   * @param appType
   * @param appName
   * @param version (optional) default to "" - unpublished if the experience is live and the language is active
   * @param lang (optional) default to english
   * @param isPreview (optional) default to false - true if the app is being rendered as preview
   * @returns
   */
  private fetchAppContent(
    brandSlug: string,
    appType: string,
    appName: string,
    lang: string,
    version: string,
    isPreview = false
  ) {
    const APP_KEY = makeStateKey<T>(
      this.getAppKey(brandSlug, appType, appName)
    );
    if (this.transferState.hasKey(APP_KEY)) {
      console.debug(`Fetching from the transfer state ${APP_KEY}`);
      const content = this.transferState.get<T>(APP_KEY, null as any as T);
      this.transferState.remove(APP_KEY);
      return of(content);
    } else {
      console.debug(`Fetching from the server ${APP_KEY}`);
      // need to fetch the content from the server
      // first determines if this app type has been migrated (need to read from insight endpoint)
      // or is legacy - need to read from legacy pwa-backend endpoint
      const queryParams = {
        lang,
        version,
      };
      if (isPreview) {
        queryParams['preview'] = isPreview;
      } else {
        queryParams['campaignId'] = this.settingsService.settings.campaignId;
        queryParams['tagId'] = this.settingsService.settings.tagId;
      }

      /**
       * If the PWA is rendered in the builder iframe, it is passed also an authToken, which is used to query the CMS API
       * and get extra "private" properties in the CMS JSON response.
       */
      if (this.settingsService.settings.viewMode === AppViewMode.BUILDER) {
        queryParams['includePrivate'] = true;
        queryParams['authToken'] = this.settingsService.settings.authToken;
        queryParams['disableDataBinding'] = true;
        // Disable conditional visibility checks in builder mode to ensure all elements are visible for editing.
        queryParams['disableConditionalVisibility'] = true;
      } else {
        queryParams['timezone'] = DateUtils.getTimezone();
      }

      /**
       * If there are external query params passed onto the app pass these params to the CMS too.
       */
      const externalQueryParams =
        this.settingsService.settings.externalQueryParams;
      if (!!externalQueryParams) {
        queryParams['externalSource'] = externalQueryParams.externalSource;
        queryParams['atmaSku'] = externalQueryParams.atmaSku;
        queryParams['atmaProductId'] = externalQueryParams.atmaProductId;
      }

      const tpmId = this.settingsService.settings.thirdPartyMappingId;
      const tpmSource = this.settingsService.settings.thirdPartyMappingSource;

      if (!!tpmId && !!tpmSource) {
        queryParams['thirdPartyMappingId'] = tpmId;
        queryParams['externalSource'] = tpmSource;
      }

      let fetch$ = this.http
        .get<{ data: T }>(
          `${environment.CMS_API_URL}/pwa-experience/${brandSlug}/${appType}/${appName}`,
          {
            params: queryParams,
            withCredentials: !isPreview,
          }
        )
        .pipe(
          map((res) => {
            return res.data;
          })
        );

      // if app is not yet migrated, fetch the content from the old legacy endpoint
      if (!environment.MIGRATED_APP_TYPES.includes(appType)) {
        fetch$ = this.fetchAppContentLegacy(
          brandSlug,
          appType,
          appName,
          lang,
          isPreview
        );
      }

      // if we have previously fetched the content of this app, we don't need to fetch it again from the server (used in reordering app for related products)
      if (this.contentCache.has(APP_KEY.toString())) {
        fetch$ = of(this.contentCache.get(APP_KEY.toString()) as T);
      }

      return fetch$.pipe(
        tap((content) => {
          content.appType = appType;

          if (isPlatformServer(this.platformId)) {
            this.transferState.set(APP_KEY, content);
          }
          this.contentCache.set(APP_KEY.toString(), content);

          if (this.settingsService.settings.viewMode !== AppViewMode.BUILDER) {
            this.preloadConnectedExperiences(content);
          }
        })
      );
    }
  }

  /**
   * Fetches content from the legacy CMS service.
   * Apps that have not been migrated to the new builder are still using this CMS service.
   *
   * @param brandSlug
   * @param appType
   * @param appName
   * @param lang
   * @param isPreview
   * @returns
   */
  fetchAppContentLegacy(
    brandSlug: string,
    appType: string,
    appName: string,
    lang: string,
    isPreview = false
  ) {
    const queryParams = {
      lang,
    };
    if (isPreview) {
      queryParams['preview'] = isPreview;
    }
    return this.http
      .get<T>(
        `${environment.CMS_API_URL}/legacy-experience/brands/${brandSlug}/types/${appType}/apps/${appName}/content`,
        {
          params: queryParams,
        }
      )
      .pipe(
        tap((content) => {
          content.brandId = brandSlug; // the legacy PWAs use brand slug as brandId
        })
      );
  }

  /**
   * Creates a unique ID for an expererience using brand slug, app type and app name.
   *
   * @param brandSlug
   * @param appType
   * @param appName
   * @returns
   */
  private getAppKey(brandSlug: string, appType: string, appName: string) {
    return `${brandSlug}-${appType}-${appName}`;
  }

  /**
   * Get observable that emits a new value whenever the app content changes,
   * but stops emitting once the app type changes, meaning it is another type of app now being loaded.
   *
   * @returns
   */
  getMainAppChanges() {
    const appType = this.appContentSource.value.appType;
    return this.appContent$.pipe(
      takeWhile((content) => content.appType === appType)
    );
  }

  /**
   * Returns a snapshot of the app content when the function is called.
   * Use the @method getMainAppChanges instead to listen to the most recent content changes.
   */
  getAppContentSnapshot() {
    return this.appContentSource.value;
  }

  /**
   * Asynchronously traverse the content object and preloads all the connected experiences.
   * When an experience is loaded, its content is saved in the session cache (emptied at every browser refresh).
   * So that if the user clicks on the links later, the TTL for loading the app is significantly decreased.
   * @param content
   */
  preloadConnectedExperiences(content: any) {
    for (var property in content) {
      if (content.hasOwnProperty(property)) {
        const value = content[property];
        if (value && typeof value == 'object') {
          if (
            !!value['type'] &&
            value['type'] === 'internal' &&
            !!value['experienceId'] &&
            !!value['url']
          ) {
            // we are now sure this is an "action" object, that contains a link to another experience, so we need to preload it.
            const appLink = value as AppLink;

            const appUrl = appLink.url;
            const [, brandSlug, appType, appName] = appUrl.split('/');
            const appKey = this.getAppKey(brandSlug, appType, appName);

            // Only fetch the app content if there is no request pending to preload the content and when the content is not yet cached
            if (
              !this.contentPreloadRequestStatus.get(appKey) &&
              !this.contentCache.has(appKey)
            ) {
              // Add the app key to the `contentPreloadRequestStatus` map to indicate that a request is ongoing to fetch the content
              this.contentPreloadRequestStatus.set(appKey, true);

              this.getAppContent(
                brandSlug,
                appType as AppType,
                appName,
                false
              ).subscribe(
                () => {
                  console.debug(`Loaded connected experience: ${appUrl}`);
                },
                () => {
                  // Set that the app failed to load
                  appLink.failedToLoad = true;
                  console.debug(
                    `Unable to load connected experience: ${appUrl}`
                  );
                },
                () => {
                  this.contentPreloadRequestStatus.delete(appKey);
                }
              );
            }
          } else {
            this.preloadConnectedExperiences(value);
          }
        }
      }
    }
  }

  /* BUILDER APP CONTENT OVERRIDE METHODS  */

  fieldChanged(propertyPath: string, propertyValue: any) {
    const appContent = this.appContentSource.getValue();
    BuilderAppContent.updateProperty(appContent, propertyPath, propertyValue);
    this.emitValueChangeOfASpecificProperty(propertyPath, propertyValue);
    this.appContentSource.next(appContent);
  }

  /**
   * Used to update multiple parameters in the builder.
   * Loops through the property array and would call the updateProperty function in
   * the BuilderAppContent for each property
   **/
  fieldsChanged(propertyArr: { propertyId: string; propertyValue: string }[]) {
    const appContent = this.appContentSource.getValue();

    propertyArr.forEach((property) => {
      const { propertyId, propertyValue } = property;
      BuilderAppContent.updateProperty(appContent, propertyId, propertyValue);
      this.emitValueChangeOfASpecificProperty(propertyId, propertyValue);
    });
    this.appContentSource.next(appContent);
  }

  listItemAdded(
    propertyPath: string,
    propertyValue: any,
    insertIndex: number
  ): void {
    const appContent = this.appContentSource.getValue();
    BuilderAppContent.listItemAdded(
      appContent,
      propertyPath,
      propertyValue,
      insertIndex
    );
    this.appContentSource.next(appContent);
  }

  listItemMoved(
    propertyPath: string,
    previousIndex: number,
    newIndex: number
  ): void {
    const appContent = this.appContentSource.getValue();
    BuilderAppContent.listItemMoved(
      appContent,
      propertyPath,
      previousIndex,
      newIndex
    );
    this.appContentSource.next(appContent);
  }

  checkIfPropertyHasValue(propertyPath: string) {
    const appContent = this.appContentSource.getValue();
    return BuilderAppContent.checkIfPropertyHasValue(appContent, propertyPath);
  }

  getPropertyValue(propertyPath: string) {
    const appContent = this.appContentSource.getValue();
    return BuilderAppContent.getPropertyValue(appContent, propertyPath);
  }

  removeProperty(propertyPath: string) {
    const appContent = this.appContentSource.getValue();
    return BuilderAppContent.removeProperty(appContent, propertyPath);
  }

  /**
   * This function would return an observable for a propertyPath.
   * By subscribing the caller could know when the value of the property path has changed.
   * The observables are extracted from the behavior subjects in the @member propertyContentChangeMap.
   * If at all there is no key instantiated for the property path that the caller wants to listen to,
   * a new behavior subject would be created, added to the map and returned to the caller.
   * The initial value of the behavior subject is set to undefined, however the caller could also pass an initial value to this function
   */
  listenToChangesInValueForASpecificProperty(
    propertyPath: string,
    initialValueToEmit?: any
  ) {
    let behaviorSubject: BehaviorSubject<any>;

    if (this.propertyContentChangeMap.has(propertyPath)) {
      behaviorSubject = this.propertyContentChangeMap.get(propertyPath);
    } else {
      behaviorSubject = new BehaviorSubject<any>(initialValueToEmit);
      this.propertyContentChangeMap.set(propertyPath, behaviorSubject);
    }

    return behaviorSubject.asObservable();
  }

  /**
   * This function would stop listening to changes in the value of a specific property path.
   * by completing the behavior subject inside the @member propertyContentChangeMap
   * and removing the property path from the map.
   */
  stopListeningToChangesInValueForASpecificProperty(propertyPath: string) {
    if (this.propertyContentChangeMap.has(propertyPath)) {
      const behaviorSubject = this.propertyContentChangeMap.get(propertyPath);
      behaviorSubject.complete();
      this.propertyContentChangeMap.delete(propertyPath);
    }
  }

  /**
   * This function would be called when a property value for a certain property path has changed.
   * If at all the @member propertyContentChangeMap contains the propertyPath, the behaviour subject
   * inside it would be made to emit the property value so that listeners can be notified about a value change.
   * As of 12th Oct 2023 this method is only called when by @methods fieldChanged and fieldsChanged
   */
  private emitValueChangeOfASpecificProperty(
    propertyPath: string,
    propertyValue: any
  ) {
    // Only emit the value if there is are listeners for the specific property
    if (this.propertyContentChangeMap.has(propertyPath)) {
      const behaviourSubject = this.propertyContentChangeMap.get(propertyPath);
      behaviourSubject.next(propertyValue);
    }
  }
}
